import { useCallback } from 'react'

import { useDebounceValue } from 'usehooks-ts'

import type { SelectOption } from '../types'

export type UseLocalFilterOptionsParams = {
  /** Current search value */
  searchValue: string
  /** Filter configuration */
  searchBy?: 'label' | 'value' | 'both'
  /** Custom filter function */
  searchFilter?: (option: SelectOption, search: string) => boolean
  /** Debounce delay in ms */
  debounceDelay?: number
}

export type UseLocalFilterOptionsReturn = (options: SelectOption[]) => SelectOption[]

/**
 * Hook to manage local options filtering with debounce
 *
 * Manages:
 * - Local filtering of options according to search
 * - Search debounce to optimize performance
 */
export const useLocalFilterOptions = ({
  searchValue,
  searchBy = 'label',
  searchFilter,
  debounceDelay = 200,
}: UseLocalFilterOptionsParams): UseLocalFilterOptionsReturn => {
  // Debounce search value
  const [debouncedSearchValue] = useDebounceValue(searchValue, debounceDelay)

  // Helper function to check if any extraData value matches the search
  const matchesExtraData = useCallback((option: SelectOption, searchLower: string): boolean => {
    if (!option.extraData) return false
    return Object.values(option.extraData).some(value => {
      if (value === null || value === undefined) return false
      return String(value).toLowerCase().includes(searchLower)
    })
  }, [])

  // Default filter function
  const defaultFilterFn = useCallback(
    (option: SelectOption, search: string): boolean => {
      const searchLower = search.toLowerCase()
      const extraDataMatches = matchesExtraData(option, searchLower)

      switch (searchBy) {
        case 'label':
          return option.label.toLowerCase().includes(searchLower) || extraDataMatches
        case 'value':
          return option.value.toLowerCase().includes(searchLower) || extraDataMatches
        case 'both':
          return (
            option.label.toLowerCase().includes(searchLower) ||
            option.value.toLowerCase().includes(searchLower) ||
            extraDataMatches
          )
        default:
          return false
      }
    },
    [searchBy, matchesExtraData],
  )

  const filterFn = searchFilter || defaultFilterFn

  // Filtering callback
  const filterOptions = useCallback(
    (options: SelectOption[]) => {
      if (!debouncedSearchValue) {
        return options
      }

      return options.reduce((acc, option) => {
        // If option is a group with children
        if (option.type === 'group' && option.children && option.children.length > 0) {
          // Check if group matches
          const groupMatches = filterFn(option, debouncedSearchValue)

          if (groupMatches) {
            // If group matches, include all its children
            acc.push(option)
          } else {
            // If group doesn't match, filter children
            const matchingChildren = option.children.filter(child =>
              filterFn(child, debouncedSearchValue),
            )

            // If at least one child matches, include group with only matching children
            if (matchingChildren.length > 0) {
              acc.push({ ...option, children: matchingChildren })
            }
          }
        } else if (filterFn(option, debouncedSearchValue)) {
          acc.push(option)
        }

        return acc
      }, [] as SelectOption[])
    },
    [debouncedSearchValue, filterFn],
  )

  return filterOptions
}
